import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Printer } from "lucide-react";
import { Logo } from "@/components/Logo";
import { FAIR_STATUS } from "@/components/fair/FairOrderRow";
import { tl, itemAmount, discountRate } from "@/lib/fair";

const cell = "border border-black px-2 py-1";
const keys = Object.keys(FAIR_STATUS);

export default function FairSummaryPrint() {
  const [data, setData] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/fair/orders"), api.get("/fair/products")]).then(([o, p]) => setData({ orders: o.data, products: p.data }));
  }, []);
  if (!data) return <div className="p-10 text-[#6B7280]">Yükleniyor...</div>;

  const rows = {};
  data.orders.forEach((o) => o.items.forEach((it) => {
    const r = rows[it.fair_product_id] = rows[it.fair_product_id] || { pending: 0, converted: 0, cancelled: 0, amount: 0, lines: [] };
    r[o.status] += it.koli;
    const net = itemAmount(it) * (1 - discountRate(o));
    r.amount += net;
    r.lines.push({ id: o.id, name: o.customer_name, status: o.status, koli: it.koli, amount: net, price: it.price });
  }));
  const list = data.products.filter((p) => rows[p.id]).map((p) => ({ p, ...rows[p.id], total: keys.reduce((a, k) => a + rows[p.id][k], 0) }))
    .sort((a, b) => a.p.model_code.localeCompare(b.p.model_code) || b.total - a.total);
  const sum = (k) => list.reduce((a, r) => a + r[k], 0);

  return (
    <div className="min-h-screen bg-white text-black" data-testid="fair-summary-print-page">
      <div className="max-w-[900px] mx-auto p-8 sm:p-10">
        <div className="flex justify-end mb-6 print:hidden">
          <button onClick={() => window.print()} data-testid="fair-summary-print-btn"
            className="flex items-center gap-2 bg-[#D97706] hover:bg-[#B45309] text-white px-5 h-11 rounded-md font-display uppercase tracking-wide">
            <Printer className="w-4 h-4" /> Yazdır
          </button>
        </div>
        <div className="flex justify-between items-start border-b-2 border-black pb-4 mb-5">
          <div>
            <Logo dark className="text-4xl" subtitle="Ayakkabı Üretim" testId="print-logo" />
            <h1 className="font-display text-2xl font-extrabold uppercase tracking-tight mt-4" data-testid="print-title">Fuar Model Özeti</h1>
          </div>
          <div className="text-right text-sm font-mono">
            <p>Tarih: {new Date().toLocaleDateString("tr-TR")}</p>
            <p>{data.orders.length} sipariş · {list.length} model/renk</p>
          </div>
        </div>
        <table className="w-full text-[13px] border border-black" data-testid="fair-summary-print-table">
          <thead className="bg-gray-100">
            <tr className="text-left">
              <th className={cell}>Model / Renk / Müşteri</th>
              {keys.map((k) => <th key={k} className={`${cell} text-right w-24`}>{FAIR_STATUS[k].label}</th>)}
              <th className={`${cell} text-right w-24`}>Toplam Koli</th>
              <th className={`${cell} text-right w-32`}>Tutar<span className="block text-[10px] font-normal">iskontolu, KDV hariç</span></th>
            </tr>
          </thead>
          <tbody>
            {list.map((r) => [
              <tr key={r.p.id} className="bg-gray-50 font-bold" data-testid={`fair-summary-print-row-${r.p.id}`}>
                <td className={cell}><span className="font-mono">{r.p.model_code}</span> — {r.p.model_color}</td>
                {keys.map((k) => <td key={k} className={`${cell} text-right font-mono`}>{r[k] || ""}</td>)}
                <td className={`${cell} text-right font-mono`}>{r.total}</td>
                <td className={`${cell} text-right font-mono`}>{tl(r.amount)}</td>
              </tr>,
              ...r.lines.map((l) => (
                <tr key={`${r.p.id}-${l.id}`} className="text-[12px]">
                  <td className={`${cell} pl-6`}>{l.name}</td>
                  {keys.map((k) => <td key={k} className={`${cell} text-right font-mono`}>{l.status === k ? l.koli : ""}</td>)}
                  <td className={`${cell} text-right font-mono text-gray-600`}>{l.koli}</td>
                  <td className={`${cell} text-right font-mono text-gray-600`}>{tl(l.amount)}{l.price ? <span className="block text-[10px]">{tl(l.price)}/çift</span> : null}</td>
                </tr>
              )),
            ])}
            <tr className="font-bold bg-gray-100">
              <td className={`${cell} text-right`}>GENEL TOPLAM</td>
              {keys.map((k) => <td key={k} className={`${cell} text-right font-mono`}>{sum(k)}</td>)}
              <td className={`${cell} text-right font-mono`} data-testid="fair-summary-print-total">{sum("total")}</td>
              <td className={`${cell} text-right font-mono`} data-testid="fair-summary-print-amount">{tl(sum("amount"))}</td>
            </tr>
          </tbody>
        </table>
        {list.length === 0 && <p className="text-sm mt-4">Henüz fuar siparişi yok.</p>}
      </div>
    </div>
  );
}
